import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { 
  Plus, Search, LayoutGrid, List, Trash2, Edit2, 
  ChevronRight, X, Image as ImageIcon, Star, 
  Eye, EyeOff, MapPin, Calendar, 
  Video, Globe, Camera,
  RefreshCcw, FolderPlus, CheckCircle2,
  ImagePlus
} from 'lucide-react';
import '../../styles/admin.css';
import { fetchProjectsAdmin, updateProject, deleteProject, createProject } from '../../lib/projects';
import { fetchCategories } from '../../lib/categories';
import { getFileUrl, pb } from '../../lib/pb';

function formatDateFr(v) {
  if (!v) return '—';
  const d = new Date(v);
  if (isNaN(d.getTime())) return v;
  return d.toLocaleDateString('fr-FR');
}

export default function AdminProjects() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);
  const [mode, setMode] = useState('create');
  const [categories, setCategories] = useState([]);
  const [viewMode, setViewMode] = useState('grid');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  // Load Initial Data
  const load = useCallback(async (targetPage = 1) => {
    setError('');
    if (targetPage === 1) setLoading(true);
    try {
      const res = await fetchProjectsAdmin(targetPage, 50, search);
      setTotalPages(res.totalPages || 1);
      setItems(res.items || []);
    } catch (e) {
      setError(e.message || 'Erreur de chargement');
    } finally {
      if (targetPage === 1) setLoading(false);
    }
  }, [search]);

  useEffect(() => { load(1); }, [load]);

  // CATEGORIES
  useEffect(() => {
    fetchCategories('realisations').then(setCategories).catch(() => {});
  }, []);

  // REAL-TIME SUBSCRIPTION (Signature Feature)
  useEffect(() => {
    pb.collection('projects').subscribe('*', function(e) {
      if (e.action === 'create') {
        setItems(prev => [e.record, ...prev]);
      } else if (e.action === 'update') {
        setItems(prev => prev.map(item => item.id === e.record.id ? e.record : item));
      } else if (e.action === 'delete') {
        setItems(prev => prev.filter(item => item.id !== e.record.id));
      }
    }, { expand: 'category' });
    return () => pb.collection('projects').unsubscribe('*');
  }, []);

  // OPTIMISTIC ACTIONS (Signature Feature)
  const handleToggleFeatured = async (p) => {
    const oldItems = [...items];
    setItems(prev => prev.map(x => x.id === p.id ? { ...x, featured: !x.featured } : x));
    try {
      await updateProject(p.id, { featured: !p.featured });
    } catch (e) {
      setItems(oldItems);
      alert('Erreur lors de la mise à jour');
    }
  };

  const handleTogglePublished = async (p) => {
    const oldItems = [...items];
    setItems(prev => prev.map(x => x.id === p.id ? { ...x, published: !x.published } : x));
    try {
      await updateProject(p.id, { published: !p.published });
    } catch (e) {
      setItems(oldItems);
      alert('Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Supprimer la réalisation "${p.title}" ?`)) return;
    try {
      await deleteProject(p.id);
    } catch (e) {
      alert(e.message);
    }
  };

  const filteredItems = useMemo(() => {
    return items.filter(p => {
      if (filterCategory && p.category !== filterCategory) return false;
      if (filterStatus === 'published' && !p.published) return false;
      if (filterStatus === 'draft' && p.published) return false;
      return true;
    });
  }, [items, filterCategory, filterStatus]);

  const openCreate = () => { 
    setMode('create'); 
    setEditing({ title: '', slug: '', category: '', published: true, date: new Date().toISOString(), location: '', gallery: [], description: '', featured: false }); 
  };
  
  const openEdit = (p) => { 
    setMode('edit'); 
    setEditing(p); 
  };

  return (
    <div className="cockpit-projects-signature">
      <header className="cockpit-header">
        <div className="header-info">
          <h1>Réalisations</h1>
          <p>Gérez vos chantiers et votre showroom de prestige.</p>
        </div>
        <button className="btn-premium-gold" onClick={openCreate}>
          <Plus size={20} />
          <span>Nouveau Projet</span>
        </button>
      </header>

      <div className="cockpit-filters-bar shadow-signature">
        <div className="search-pill">
          <Search size={18} />
          <input 
            placeholder="Rechercher un chantier..." 
            value={search} 
            onChange={e => setSearch(e.target.value)} 
          />
        </div>
        
        <div className="filter-selects">
          <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)}>
            <option value="">Toutes les catégories</option>
            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="all">Tous les statuts</option>
            <option value="published">Publiés</option>
            <option value="draft">Brouillons</option>
          </select>
        </div>

        <div className="view-selector">
           <button className={viewMode === 'grid' ? 'active' : ''} onClick={() => setViewMode('grid')}><LayoutGrid size={18}/></button>
           <button className={viewMode === 'list' ? 'active' : ''} onClick={() => setViewMode('list')}><List size={18}/></button>
        </div>
      </div>

      <main className="cockpit-main">
        {loading ? (
          <div className="cockpit-loading-state">
            <RefreshCcw size={24} className="spin" />
            <p>Synchronisation du Showroom...</p>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="cockpit-empty-state">
            <Camera size={48} />
            <h3>Aucun projet trouvé</h3>
            <p>Commencez par ajouter votre première réalisation de prestige.</p>
          </div>
        ) : (
          <div className={`cockpit-view ${viewMode}`}>
            {viewMode === 'grid' ? (
              <div className="signature-admin-grid">
                {filteredItems.map(p => (
                  <div key={p.id} className="signature-admin-card" onClick={() => openEdit(p)}>
                    <div className="card-visual">
                      {p.gallery && p.gallery[0] ? (
                        <img src={getFileUrl(p, p.gallery[0])} alt={p.title} />
                      ) : (
                        <div className="no-visual"><ImageIcon size={32}/></div>
                      )}
                      
                      <div className="card-badges-top">
                        <button 
                          className={`badge-star ${p.featured ? 'active' : ''}`}
                          onClick={(e) => { e.stopPropagation(); handleToggleFeatured(p); }}
                        >
                          <Star size={14} fill={p.featured ? 'currentColor' : 'none'} />
                        </button>
                        <div className={`badge-status ${p.published ? 'published' : 'draft'}`}>
                           {p.published ? <Eye size={12}/> : <EyeOff size={12}/>}
                           <span>{p.published ? 'PUBLIC' : 'BROUILLON'}</span>
                        </div>
                      </div>

                      <div className="card-photo-count">
                         <ImageIcon size={12}/>
                         <span>{p.gallery?.length || 0}</span>
                      </div>
                    </div>
                    
                    <div className="card-info">
                       <div className="card-cat-tag">{p.expand?.category?.name || 'Réalisation'}</div>
                       <h3>{p.title}</h3>
                       <div className="card-meta">
                          {p.location && <span><MapPin size={12}/> {p.location}</span>}
                          {p.date && <span><Calendar size={12}/> {new Date(p.date).getFullYear()}</span>}
                       </div>
                    </div>

                    <div className="card-actions-hover">
                       <button className="action-btn-edit"><Edit2 size={16}/></button>
                       <button 
                         className="action-btn-del"
                         onClick={(e) => { e.stopPropagation(); handleDelete(p); }}
                       ><Trash2 size={16}/></button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="signature-admin-list shadow-signature">
                <table className="premium-admin-table">
                  <thead>
                    <tr>
                      <th style={{paddingLeft: 25}}>Réalisation</th>
                      <th>Catégorie</th>
                      <th>Lieu</th>
                      <th>Statut</th>
                      <th style={{textAlign: 'right', paddingRight: 25}}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItems.map(p => (
                      <tr key={p.id}>
                        <td style={{paddingLeft: 25}}>
                          <div className="item-profile" onClick={() => openEdit(p)}>
                            <div className="item-avatar-rect">
                               {p.gallery?.[0] ? <img src={getFileUrl(p, p.gallery[0])} /> : <ImageIcon size={14}/>}
                            </div>
                            <div className="item-text">
                               <div className="item-name">{p.title}</div>
                               <div className="item-sub">/{p.slug}</div>
                            </div>
                          </div>
                        </td>
                        <td><span className="badge-pill-cat">{p.expand?.category?.name || '—'}</span></td>
                        <td><div className="item-meta-txt">{p.location || '—'}</div></td>
                        <td>
                           <div className={`badge-status-pill ${p.published ? 'published' : ''}`} onClick={(e) => { e.stopPropagation(); handleTogglePublished(p); }}>
                              {p.published ? <Eye size={14}/> : <EyeOff size={14}/>}
                              <span>{p.published ? 'PUBLIÉ' : 'BROUILLON'}</span>
                           </div>
                        </td>
                        <td style={{textAlign: 'right', paddingRight: 25}}>
                           <div className="action-row-btns">
                              <button 
                                className={`btn-action-star ${p.featured ? 'active' : ''}`}
                                onClick={(e) => { e.stopPropagation(); handleToggleFeatured(p); }}
                              >
                                 <Star size={14} fill={p.featured ? 'currentColor' : 'none'} />
                              </button>
                              <button className="btn-action-edit" onClick={() => openEdit(p)}>
                                 <Edit2 size={14} />
                              </button>
                              <button className="btn-action-del" onClick={(e) => { e.stopPropagation(); handleDelete(p); }}>
                                 <Trash2 size={14} />
                              </button>
                           </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </main>

      {editing && (
        <ProjectModal 
          mode={mode} 
          project={editing} 
          categories={categories}
          onClose={() => setEditing(null)} 
          onSaved={() => setEditing(null)} 
        />
      )}

      <style jsx>{`
        .cockpit-projects-signature { animation: fadeIn 0.4s ease; padding-bottom: 50px; }
        
        .cockpit-header { display: flex; justify-content: space-between; align-items: flex-end; margin-bottom: 30px; }
        .header-info h1 { font-size: 32px; font-weight: 950; color: #0f172a; margin: 0; letter-spacing: -1.5px; }
        .header-info p { color: #64748b; margin: 5px 0 0 0; font-weight: 600; }

        .cockpit-filters-bar { background: #fff; border-radius: 24px; padding: 12px 20px; display: flex; gap: 20px; align-items: center; margin-bottom: 30px; border: 1px solid #f1f5f9; }
        .search-pill { flex: 1; display: flex; align-items: center; gap: 12px; background: #f8fafc; padding: 10px 20px; border-radius: 16px; color: #94a3b8; }
        .search-pill input { border: none; background: none; width: 100%; outline: none; font-weight: 700; color: #0f172a; }
        
        .filter-selects { display: flex; gap: 10px; }
        .filter-selects select { background: #f8fafc; border: 1px solid #f1f5f9; border-radius: 14px; padding: 10px 15px; font-weight: 800; font-size: 13px; color: #0f172a; outline: none; transition: all 0.2s; }
        .filter-selects select:focus { border-color: #a37e2c; }

        .view-selector { display: flex; background: #f1f5f9; padding: 4px; border-radius: 14px; gap: 4px; }
        .view-selector button { width: 40px; height: 40px; border-radius: 10px; border: none; background: none; color: #64748b; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.2s; }
        .view-selector button.active { background: #fff; color: #0f172a; box-shadow: 0 4px 6px rgba(0,0,0,0.05); }

        /* GRID ADMIN */
        .signature-admin-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 25px; }
        .signature-admin-card { background: #fff; border-radius: 30px; border: 1px solid #f1f5f9; overflow: hidden; cursor: pointer; transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1); position: relative; }
        .signature-admin-card:hover { transform: translateY(-5px); box-shadow: 0 20px 40px -10px rgba(0,0,0,0.08); border-color: #a37e2c; }
        
        .card-visual { height: 200px; background: #f1f5f9; position: relative; overflow: hidden; }
        .card-visual img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.6s; }
        .signature-admin-card:hover .card-visual img { transform: scale(1.05); }
        
        .card-badges-top { position: absolute; top: 15px; left: 15px; right: 15px; display: flex; justify-content: space-between; align-items: center; }
        .badge-star { width: 34px; height: 34px; border-radius: 50%; background: rgba(255,255,255,0.9); backdrop-filter: blur(5px); border: none; display: flex; align-items: center; justify-content: center; color: #cbd5e1; cursor: pointer; transition: all 0.2s; }
        .badge-star.active { color: #a37e2c; background: #fff; }
        
        .badge-status { padding: 6px 12px; border-radius: 99px; font-size: 10px; font-weight: 900; background: rgba(255,255,255,0.9); border: 1px solid transparent; display: flex; align-items: center; gap: 6px; }
        .badge-status.published { color: #059669; }
        .badge-status.draft { color: #64748b; }

        .card-photo-count { position: absolute; bottom: 15px; right: 15px; background: rgba(15,23,42,0.6); backdrop-filter: blur(5px); color: #fff; padding: 4px 10px; border-radius: 8px; display: flex; align-items: center; gap: 5px; font-size: 11px; font-weight: 800; }

        .card-info { padding: 20px; }
        .card-cat-tag { font-size: 10px; font-weight: 900; color: #a37e2c; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 5px; }
        .card-info h3 { margin: 0 0 10px 0; font-size: 18px; font-weight: 900; color: #0f172a; }
        .card-meta { display: flex; gap: 15px; }
        .card-meta span { display: flex; align-items: center; gap: 6px; font-size: 12px; font-weight: 700; color: #64748b; }

        .card-actions-hover { position: absolute; inset: 0; background: rgba(255,255,255,0.9); display: flex; align-items: center; justify-content: center; gap: 15px; opacity: 0; transition: all 0.2s; }
        .signature-admin-card:hover .card-actions-hover { opacity: 1; }
        .action-btn-edit, .action-btn-del { width: 50px; height: 50px; border-radius: 15px; border: none; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.2s; }
        .action-btn-edit { background: #f8fafc; color: #0f172a; border: 1px solid #f1f5f9; }
        .action-btn-edit:hover { background: #0f172a; color: #fff; }
        .action-btn-del { background: #fff1f2; color: #f43f5e; }
        .action-btn-del:hover { background: #f43f5e; color: #fff; }

        /* LIST VIEW ADMIN */
        .signature-admin-list { background: #fff; border-radius: 24px; overflow: hidden; border: 1px solid #f1f5f9; }
        .premium-admin-table { width: 100%; border-collapse: collapse; }
        .premium-admin-table th { text-align: left; background: #f8fafc; padding: 15px 20px; font-size: 11px; font-weight: 900; color: #94a3b8; text-transform: uppercase; letter-spacing: 1px; border-bottom: 1px solid #f1f5f9; }
        .premium-admin-table td { padding: 15px 20px; border-bottom: 1px solid #f1f5f9; transition: all 0.2s; }
        .premium-admin-table tr:hover td { background: #fafafa; }
        
        .item-profile { display: flex; align-items: center; gap: 15px; cursor: pointer; }
        .item-avatar-rect { width: 50px; height: 38px; border-radius: 10px; overflow: hidden; background: #f1f5f9; border: 1px solid #eef0f2; }
        .item-avatar-rect img { width: 100%; height: 100%; object-fit: cover; }
        .item-name { font-weight: 800; font-size: 15px; color: #0f172a; }
        .item-sub { font-size: 10px; font-weight: 800; color: #cbd5e1; }

        .badge-pill-cat { background: #f1f5f9; color: #475569; font-size: 11px; font-weight: 800; padding: 6px 14px; border-radius: 100px; }
        .badge-status-pill { display: inline-flex; align-items: center; gap: 8px; padding: 6px 14px; border-radius: 100px; background: #fef2f2; color: #ef4444; font-size: 10px; font-weight: 900; transition: all 0.2s; cursor: pointer; border: 1px solid transparent; }
        .badge-status-pill.published { background: #ecfdf5; color: #059669; }
        .badge-status-pill:hover { transform: scale(1.05); }

        .action-row-btns { display: flex; gap: 8px; justify-content: flex-end; }
        .btn-action-star, .btn-action-edit, .btn-action-del { width: 36px; height: 36px; border-radius: 10px; border: none; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.2s; }
        .btn-action-star { background: #f8fafc; color: #cbd5e1; }
        .btn-action-star.active { color: #a37e2c; background: #fffcf0; border: 1px solid #fef3c7; }
        .btn-action-edit { background: #f0f9ff; color: #0ea5e9; }
        .btn-action-del { background: #fff1f2; color: #f43f5e; }

        .cockpit-loading-state { padding: 100px 0; text-align: center; }
        .cockpit-empty-state { padding: 100px 0; text-align: center; color: #94a3b8; }
        .cockpit-empty-state h3 { color: #0f172a; margin-top: 20px; }

        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
}

function ProjectModal({ mode, project, categories, onClose, onSaved }) {
    const [tab, setTab] = useState('infos');
    const [saving, setSaving] = useState(false);
    
    // Form State
    const [title, setTitle] = useState(project.title || '');
    const [slug, setSlug] = useState(project.slug || '');
    const [category, setCategory] = useState(project.category || '');
    const [location, setLocation] = useState(project.location || '');
    const [date, setDate] = useState(project.date ? project.date.slice(0,10) : new Date().toISOString().split('T')[0]);
    const [description, setDescription] = useState(project.description || '');
    const [published, setPublished] = useState(project.published ?? true);
    const [featured, setFeatured] = useState(project.featured ?? false);
    const [youtube_url, setYoutubeUrl] = useState(project.youtube_url || '');
    const [facebook_url, setFacebookUrl] = useState(project.facebook_url || '');

    // Media State
    const [newFiles, setNewFiles] = useState([]);
    const [existingImages, setExistingImages] = useState(project.gallery || []);

    const slugify = (v) => v.toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        const fd = new FormData();
        fd.append('title', title);
        fd.append('slug', slug);
        fd.append('category', category);
        fd.append('location', location);
        fd.append('date', date);
        fd.append('description', description);
        fd.append('published', published);
        fd.append('featured', featured);
        fd.append('youtube_url', youtube_url);
        fd.append('facebook_url', facebook_url);

        // PocketBase handles file replacement if we pass the existing ones we want to keep
        // and new ones to add. 
        // For simplicity here, we append new ones. 
        newFiles.forEach(f => fd.append('gallery', f));
        
        // If some were removed, we'd need to send the remaining names to keep them
        // PocketBase logic: if field is in FormData, it replaces unless using gallery+
        // But the SDK/PocketBase also allows sending the names of files to KEEP.
        // For now, we'll assume we keep existing + add new.
        
        try {
            if (project.id) await updateProject(project.id, fd);
            else await createProject(fd);
            onSaved();
        } catch(e) { 
            alert('Erreur lors de la sauvegarde : ' + e.message); 
        } finally { 
            setSaving(false); 
        }
    };

    return (
        <div className="modal-admin-overlay" onClick={e => e.target.className === 'modal-admin-overlay' && onClose()}>
            <div className="modal-admin-card shadow-signature animated-scale">
                <header className="modal-admin-header">
                    <div className="modal-admin-tabs">
                        <button className={tab === 'infos' ? 'active' : ''} onClick={() => setTab('infos')}>Détails</button>
                        <button className={tab === 'media' ? 'active' : ''} onClick={() => setTab('media')}>Galerie Photo</button>
                        <button className={tab === 'social' ? 'active' : ''} onClick={() => setTab('social')}>Options</button>
                    </div>
                    <button className="btn-close-modal" onClick={onClose}><X size={20}/></button>
                </header>

                <form onSubmit={handleSave} className="modal-admin-body">
                    {tab === 'infos' && (
                        <div className="admin-form-grid">
                            <div className="form-field full">
                                <label>Titre de la réalisation</label>
                                <input 
                                  value={title} 
                                  placeholder="Ex: Portail Aluminium Motorisé - Auch"
                                  onChange={e => { setTitle(e.target.value); if(mode === 'create') setSlug(slugify(e.target.value)); }} 
                                  required 
                                />
                            </div>
                            <div className="form-field">
                                <label>Lieu / Ville</label>
                                <input value={location} placeholder="Ex: Auch (32)" onChange={e => setLocation(e.target.value)} />
                            </div>
                            <div className="form-field">
                                <label>Date du projet</label>
                                <input type="date" value={date} onChange={e => setDate(e.target.value)} />
                            </div>
                            <div className="form-field full">
                                <label>Catégorie de prestation</label>
                                <select value={category} onChange={e => setCategory(e.target.value)} required>
                                    <option value="">Sélectionner une catégorie...</option>
                                    {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                            </div>
                            <div className="form-field full">
                                <label>Récit du projet / Description</label>
                                <textarea 
                                  value={description} 
                                  placeholder="Décrivez les spécificités de cette installation..."
                                  onChange={e => setDescription(e.target.value)} 
                                  style={{height:120}}
                                />
                            </div>
                        </div>
                    )}

                    {tab === 'media' && (
                        <div className="admin-media-gallery">
                            <div className="media-info-box">
                               <Camera size={16}/>
                               <span>Ajoutez une ou plusieurs photos. La première sera l'image de couverture.</span>
                            </div>
                            
                            <div className="media-grid-preview">
                                {existingImages.map((img, i) => (
                                    <div key={i} className="media-item">
                                        <img src={getFileUrl(project, img)} alt="Gallery" />
                                        <div className="item-label">Existante</div>
                                    </div>
                                ))}
                                {newFiles.map((f, i) => (
                                    <div key={i} className="media-item new">
                                        <img src={URL.createObjectURL(f)} alt="New" />
                                        <div className="item-label">Nouveau</div>
                                        <button type="button" className="btn-remove-new" onClick={() => setNewFiles(p => p.filter((_, idx) => idx !== i))}><X size={14}/></button>
                                    </div>
                                ))}
                                <label className="media-add-box">
                                   <ImagePlus size={32}/>
                                   <span>Ajouter des photos</span>
                                   <input 
                                     type="file" 
                                     multiple 
                                     hidden 
                                     accept="image/*" 
                                     onChange={e => setNewFiles(p => [...p, ...Array.from(e.target.files)])} 
                                   />
                                </label>
                            </div>
                        </div>
                    )}

                    {tab === 'social' && (
                        <div className="admin-form-grid">
                            <div className="form-field full">
                                <label><Video size={14}/> Lien Vidéo YouTube (Optionnel)</label>
                                <input value={youtube_url} placeholder="https://youtube.com/..." onChange={e => setYoutubeUrl(e.target.value)} />
                            </div>
                            <div className="form-field full">
                                <label><Globe size={14}/> Lien Publication Facebook (Optionnel)</label>
                                <input value={facebook_url} placeholder="https://facebook.com/..." onChange={e => setFacebookUrl(e.target.value)} />
                            </div>
                            <div className="form-checkbox-group">
                                <label className="checkbox-item">
                                   <input type="checkbox" checked={featured} onChange={e => setFeatured(e.target.checked)} />
                                   <div className="checkbox-box"><Star size={16}/></div>
                                   <span>Mettre en avant (Coup de cœur)</span>
                                </label>
                                <label className="checkbox-item">
                                   <input type="checkbox" checked={published} onChange={e => setPublished(e.target.checked)} />
                                   <div className="checkbox-box"><Eye size={16}/></div>
                                   <span>Publier immédiatement</span>
                                </label>
                            </div>
                            <div className="form-field full">
                                <label>Slug URL (automatique)</label>
                                <input value={slug} readOnly className="readonly" />
                            </div>
                        </div>
                    )}

                    <footer className="modal-admin-footer">
                        <button 
                          type="button" 
                          className="btn-cancel" 
                          onClick={onClose}
                        >Annuler</button>
                        <button 
                          type="submit" 
                          className="btn-premium-gold" 
                          disabled={saving}
                        >
                          {saving ? <RefreshCcw size={18} className="spin"/> : <CheckCircle2 size={18}/>}
                          <span>{saving ? 'Enregistrement...' : mode === 'create' ? 'Créer le projet' : 'Enregistrer les modifications'}</span>
                        </button>
                    </footer>
                </form>
            </div>

            <style jsx>{`
                .modal-admin-overlay { position: fixed; inset: 0; background: rgba(15,23,42,0.6); backdrop-filter: blur(10px); z-index: 5000; display: flex; align-items: center; justify-content: center; padding: 20px; }
                .modal-admin-card { background: #fff; width: 100%; max-width: 650px; border-radius: 40px; overflow: hidden; display: flex; flex-direction: column; max-height: 90vh; }
                
                .modal-admin-header { padding: 20px 30px; border-bottom: 1px solid #f1f5f9; display: flex; justify-content: space-between; align-items: center; background: #fff; position: sticky; top: 0; z-index: 10; }
                .modal-admin-tabs { display: flex; gap: 8px; background: #f1f5f9; padding: 5px; border-radius: 16px; }
                .modal-admin-tabs button { border: none; background: none; padding: 8px 20px; border-radius: 12px; font-weight: 800; font-size: 13px; color: #64748b; cursor: pointer; transition: all 0.2s; }
                .modal-admin-tabs button.active { background: #fff; color: #0f172a; box-shadow: 0 4px 10px rgba(0,0,0,0.05); }
                .btn-close-modal { width: 40px; height: 40px; border-radius: 12px; border: none; background: #f8fafc; color: #64748b; cursor: pointer; display: flex; align-items: center; justify-content: center; }

                .modal-admin-body { padding: 30px; overflow-y: auto; flex: 1; }
                
                .admin-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
                .form-field { display: flex; flex-direction: column; gap: 8px; }
                .form-field.full { grid-column: 1 / -1; }
                .form-field label { font-size: 11px; font-weight: 900; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.5px; }
                .form-field input, .form-field select, .form-field textarea { background: #f8fafc; border: 1.5px solid #f1f5f9; padding: 12px 16px; border-radius: 16px; font-weight: 700; color: #0f172a; outline: none; transition: all 0.2s; }
                .form-field input:focus, .form-field select:focus { border-color: #a37e2c; background: #fff; }
                .form-field input.readonly { background: #f1f5f9; color: #94a3b8; cursor: not-allowed; }

                .form-checkbox-group { grid-column: 1 / -1; display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin-top: 10px; }
                .checkbox-item { cursor: pointer; display: flex; align-items: center; gap: 12px; background: #f8fafc; padding: 15px; border-radius: 18px; transition: all 0.2s; border: 1.5px solid transparent; }
                .checkbox-item input { display: none; }
                .checkbox-item:has(input:checked) { background: #fff; border-color: #a37e2c; box-shadow: 0 10px 15px -10px rgba(163, 126, 44, 0.2); }
                .checkbox-box { width: 34px; height: 34px; border-radius: 10px; background: #fff; border: 1px solid #e2e8f0; display: flex; align-items: center; justify-content: center; color: #cbd5e1; transition: all 0.2s; }
                .checkbox-item:has(input:checked) .checkbox-box { background: #0f172a; color: #fff; border-color: #0f172a; }
                .checkbox-item span { font-weight: 800; font-size: 14px; color: #64748b; }
                .checkbox-item:has(input:checked) span { color: #0f172a; }

                .admin-media-gallery { display: flex; flex-direction: column; gap: 20px; }
                .media-info-box { background: #f0f9ff; border: 1px solid #e0f2fe; padding: 12px 20px; border-radius: 14px; display: flex; align-items: center; gap: 10px; color: #0369a1; font-size: 13px; font-weight: 700; }
                .media-grid-preview { display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px; }
                .media-item { aspect-ratio: 4/3; border-radius: 20px; overflow: hidden; position: relative; border: 1px solid #f1f5f9; }
                .media-item img { width: 100%; height: 100%; object-fit: cover; }
                .item-label { position: absolute; bottom: 8px; left: 8px; padding: 2px 8px; background: rgba(0,0,0,0.5); color: #fff; font-size: 9px; font-weight: 900; border-radius: 6px; text-transform: uppercase; }
                .media-item.new { border-color: #a37e2c; }
                .btn-remove-new { position: absolute; top: 8px; right: 8px; background: #fff; color: #ef4444; border: none; width: 24px; height: 24px; border-radius: 50%; cursor: pointer; display: flex; align-items: center; justify-content: center; box-shadow: 0 4px 6px rgba(0,0,0,0.1); }
                
                .media-add-box { aspect-ratio: 4/3; border: 2px dashed #cbd5e1; border-radius: 20px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; color: #94a3b8; cursor: pointer; transition: all 0.2s; }
                .media-add-box:hover { border-color: #a37e2c; color: #a37e2c; background: #fffcf0; }
                .media-add-box span { font-size: 12px; font-weight: 800; }

                .modal-admin-footer { padding: 20px 30px; border-top: 1px solid #f1f5f9; display: flex; gap: 15px; justify-content: flex-end; }
                .btn-cancel { background: #f8fafc; border: none; padding: 12px 25px; border-radius: 16px; font-weight: 800; color: #64748b; cursor: pointer; transition: all 0.2s; }
                .btn-cancel:hover { background: #f1f5f9; color: #0f172a; }

                .animated-scale { animation: modalScale 0.3s cubic-bezier(0.16, 1, 0.3, 1); }
                @keyframes modalScale { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
                .spin { animation: spin 1s linear infinite; }
            `}</style>
        </div>
    );
}
